import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ApiRequestError, useApi } from "./api";
import { useAuthentication } from "./auth";
import { AppShell } from "./components/AppShell";
import { ButtonLoader } from "./components/Ui";
import {
  AdminAuditPage,
  AccessManagementPage,
  DashboardPage,
  DependenciesPage,
  ExecutivePage,
  GovernancePage,
  InitiativeDetailPage,
  InitiativesPage,
  IntakePage,
  NotificationsPage,
  RoadmapPage,
  SettingsPage,
} from "./pages";
import type { User } from "./types";
import { ReportsPage } from "./components/ReportsPage";
import { CapacityPage } from "./components/CapacityPage";
import { PermissionsPage } from "./components/PermissionsPage";
import { usePermissions } from "./permissions";
import { ProductRegister } from "./components/ProductRegister";

export function App() {
  const api = useApi();
  const auth = useAuthentication();
  const permissions = usePermissions(auth.isAuthenticated);
  const queryClient = useQueryClient();
  const accessSignature = permissions.data
    ? JSON.stringify(permissions.data)
    : undefined;
  const previousAccess = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!accessSignature) return;
    if (previousAccess.current && previousAccess.current !== accessSignature) {
      void queryClient.resetQueries({
        predicate: (query) => query.queryKey[0] !== "permissions-me",
      });
    }
    previousAccess.current = accessSignature;
  }, [accessSignature, queryClient]);
  const session = useQuery({
    queryKey: ["session"],
    queryFn: () => api.post<{ user: User }>("/auth/session"),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: auth.isAuthenticated,
    retry: false,
  });

  if (!auth.isAuthenticated) {
    return <AuthenticationScreen busy={auth.isBusy} onSignIn={auth.signIn} />;
  }

  if (session.isPending) {
    return (
      <div className="app-state">
        <span className="spinner" /> Signing you in…
      </div>
    );
  }
  if (session.isError) {
    if (
      (session.error instanceof ApiRequestError || "status" in session.error) &&
      (session.error as { status?: number }).status === 401
    ) {
      return <AuthenticationScreen busy={auth.isBusy} onSignIn={auth.signIn} />;
    }
    if (
      session.error instanceof ApiRequestError &&
      session.error.code === "ACCESS_NOT_PROVISIONED"
    ) {
      return (
        <AccessDeniedScreen
          email={auth.accountEmail}
          busy={auth.isBusy}
          onSignOut={auth.signOut}
        />
      );
    }
    return (
      <div className="app-state error-state" role="alert">
        <h1>Unable to open Strategic Dashboard</h1>
        <p>{session.error.message}</p>
        <button
          className="btn-primary"
          disabled={session.isFetching}
          onClick={() => void session.refetch()}
        >
          {session.isFetching ? (
            <ButtonLoader label="Trying again…" />
          ) : (
            "Try again"
          )}
        </button>
      </div>
    );
  }

  if (permissions.isPending)
    return (
      <div className="app-state">
        <span className="spinner" /> Loading permissions…
      </div>
    );

  if (permissions.isError)
    return (
      <div className="app-state error-state" role="alert">
        <h1>Unable to verify permissions</h1>
        <p>Restricted information is hidden until access can be verified.</p>
        <button
          className="btn-primary"
          onClick={() => void permissions.refetch()}
        >
          Try again
        </button>
      </div>
    );

  if (permissions.data?.accessPending) return (
    <div className="app-state">
      <img src="/payangel-logo.png" alt="PayAngel" className="auth-logo" />
      <h1>Welcome to Strategic Dashboard</h1>
      <p>Your account is authorised. An administrator still needs to assign your role and department access.</p>
      <p>You will receive an invitation email once your role is assigned.</p>
      <button className="btn-primary pending-access-primary" onClick={() => void permissions.refetch()}>Check access</button>
      <button className="btn-outline" onClick={() => { queryClient.clear(); void auth.signOut(); }}>Sign out</button>
    </div>
  );

  return (
    <AppShell
      user={session.data.user}
      onLogout={async () => {
        queryClient.clear();
        await auth.signOut();
      }}
    >
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/initiatives" element={<InitiativesPage />} />
        <Route path="/products" element={<ProductRegister />} />
        <Route
          path="/reports"
          element={
            permissions.can("executive.view") ? (
              <ReportsPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/capacity"
          element={
            permissions.isPending ? (
              <div className="app-state">Loading permissions…</div>
            ) : permissions.nativeCapacity ||
              permissions.can("capacity.view") ||
              permissions.can("integrations.manage") ? (
              <CapacityPage user={session.data.user} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/settings/permissions"
          element={
            permissions.isPending ? (
              <div className="app-state">Loading permissions…</div>
            ) : permissions.can("roles.manage") ? (
              <PermissionsPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/initiatives/:id"
          element={<InitiativeDetailPage user={session.data.user} />}
        />
        <Route
          path="/intake"
          element={
            permissions.can("initiatives.create") ? (
              <IntakePage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/dependencies" element={<DependenciesPage />} />
        <Route path="/roadmap" element={<RoadmapPage />} />
        <Route
          path="/governance"
          element={
            permissions.can("governance.view") ? (
              <GovernancePage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/executive"
          element={
            permissions.can("executive.view") ? (
              <ExecutivePage user={session.data.user} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/settings"
          element={<SettingsPage user={session.data.user} />}
        />
        <Route
          path="/admin/audit"
          element={
            permissions.can("audit.view") ? (
              <AdminAuditPage />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/admin/access"
          element={
            permissions.isPending ? (
              <div className="app-state">Loading permissions…</div>
            ) : permissions.can("roles.manage") ? (
              <AccessManagementPage currentUser={session.data.user} />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}

function AuthenticationScreen({
  busy,
  onSignIn,
}: {
  busy: boolean;
  onSignIn(): Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  return (
    <main className="auth-page">
      <section className="auth-panel" aria-labelledby="sign-in-title">
        <img src="/payangel-logo.png" alt="PayAngel" className="auth-logo" />
        <span className="auth-eyebrow">Strategic Prioritisation</span>
        <h1 id="sign-in-title">Sign in to continue</h1>
        <p>
          Use your PayAngel Microsoft account. Access must be approved by a
          Strategic Dashboard super administrator.
        </p>
        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}
        <button
          className="microsoft-signin"
          disabled={busy}
          onClick={() => {
            setError(null);
            void onSignIn().catch(() =>
              setError(
                "Microsoft sign-in could not be completed. Please try again.",
              ),
            );
          }}
        >
          {busy ? (
            <ButtonLoader label="Opening Microsoft sign-in…" />
          ) : (
            <>
              <span className="microsoft-mark" aria-hidden="true">
                <i />
                <i />
                <i />
                <i />
              </span>
              Sign in with Microsoft
            </>
          )}
        </button>
        <div className="auth-security-note">
          <ShieldCheck size={18} />
          <span>
            Protected by Microsoft Entra ID, MFA, and organisational access
            controls.
          </span>
        </div>
      </section>
    </main>
  );
}

function AccessDeniedScreen({
  email,
  busy,
  onSignOut,
}: {
  email: string | null;
  busy: boolean;
  onSignOut(): Promise<void>;
}) {
  return (
    <main className="auth-page">
      <section className="auth-panel access-denied" role="alert">
        <img src="/payangel-logo.png" alt="PayAngel" className="auth-logo" />
        <span className="auth-status-icon">
          <ShieldCheck size={26} />
        </span>
        <h1>Access has not been enabled</h1>
        <p>
          Your Microsoft identity was verified, but this account is not on the
          Strategic Dashboard access list.
        </p>
        {email && <code className="auth-account">{email}</code>}
        <p className="muted">
          Ask a super administrator to add this email address, then sign in
          again.
        </p>
        <button
          className="btn-outline"
          disabled={busy}
          onClick={() => void onSignOut()}
        >
          Sign out and use another account
        </button>
      </section>
    </main>
  );
}
